/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useState, useEffect } from "react";
import { useStoreDispatch } from "../../../../store/hooks";
import { setBasicDialogOpenFor } from "../../../../store/helpers/helperReducers";
import { IThresholdCrossingSettingSchedule } from "../../../../store/api/admin/types";
import { useThresholdCrossingSettingScheduleData } from "./hooks";
import dayjs, { Dayjs } from "dayjs";
import { isError } from "../../../../utils/validations";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { Switch } from "../../../ui/switch";
import { Input } from "../../../ui/input";

interface IAddEditThresholdCrossingScheduleProps {
  selectedRow: IThresholdCrossingSettingSchedule;
  modelOpenMode?: string;
  scheduleList: IThresholdCrossingSettingSchedule[];
}

const parseScheduleTime = (time: string | Dayjs | null | undefined) => {
  if (!time) {
    return dayjs("");
  }
  if (dayjs.isDayjs(time)) {
    return time;
  }

  const normalized = String(time).trim();

  const as24Hour = dayjs(normalized, "HH:mm", true);
  if (as24Hour.isValid()) return as24Hour;

  const as24HourWithSeconds = dayjs(normalized, "HH:mm:ss", true);
  if (as24HourWithSeconds.isValid()) return as24HourWithSeconds;

  const as12Hour = dayjs(normalized, "hh:mm A", true);
  if (as12Hour.isValid()) return as12Hour;

  const asIsoDateTime = dayjs(normalized);
  if (asIsoDateTime.isValid()) return asIsoDateTime;

  const timeMatch = normalized.match(/(\d{2}:\d{2})(?::\d{2})?/);
  if (timeMatch?.[1]) {
    const fromExtracted = dayjs(timeMatch[1], "HH:mm", true);
    if (fromExtracted.isValid()) return fromExtracted;
  }

  return dayjs("");
};

const AddEditProfileSchedule: React.FC<IAddEditThresholdCrossingScheduleProps> = ({ modelOpenMode = "Add", selectedRow, scheduleList }) => {
  const dispatch = useStoreDispatch();
  const {
    thresholdCrossingSettingScheduleData,
    setThresholdCrossingSettingScheduleData,
    submitHandler,
    errMsg,
    setErrMsg,
    errorsPresent,
    days,
    fromTimes,
    toTimes
  } = useThresholdCrossingSettingScheduleData(selectedRow);

  const isDisabledSave = !(!!thresholdCrossingSettingScheduleData?.days.length && !!thresholdCrossingSettingScheduleData?.fromTime && !!thresholdCrossingSettingScheduleData?.toTime) || isError(errMsg);
  const daysOfWeek = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const dayDisplayMap: { [key: string]: string } = {
    "MON": "M",
    "TUE": "T",
    "WED": "W",
    "THU": "T",
    "FRI": "F",
    "SAT": "S",
    "SUN": "S"
  };
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const isAlreadyAllDay =
    parseScheduleTime(thresholdCrossingSettingScheduleData?.fromTime || "").format("HH:mm") === "00:00" &&
    parseScheduleTime(thresholdCrossingSettingScheduleData?.toTime || "").format("HH:mm") === "23:59";
  const [isAllDay, setIsAllDay] = useState<boolean>(modelOpenMode === "Add" ? true : isAlreadyAllDay);

  useEffect(() => {
    if (modelOpenMode === "Add") {
      setThresholdCrossingSettingScheduleData((prevData: any) => ({
        ...prevData,
        fromTime: "00:00",
        toTime: "23:59"
      }));
    }
  }, [modelOpenMode, setThresholdCrossingSettingScheduleData]);

  const isDirty = modelOpenMode === "Add" || (
    JSON.stringify([...thresholdCrossingSettingScheduleData.days].sort()) !== JSON.stringify([...selectedRow.days].sort()) ||
    thresholdCrossingSettingScheduleData.fromTime !== selectedRow.fromTime ||
    thresholdCrossingSettingScheduleData.toTime !== selectedRow.toTime
  );

  const handleAllDayChange = (checked: boolean) => {
    setIsAllDay(checked);
    if (checked) {
      setThresholdCrossingSettingScheduleData({
        ...thresholdCrossingSettingScheduleData,
        fromTime: "00:00",
        toTime: "23:59"
      });
      setErrMsg({ ...errMsg, fromTime: "", toTime: "" });
    } else {
      const currentTime = dayjs();
      setThresholdCrossingSettingScheduleData({
        ...thresholdCrossingSettingScheduleData,
        fromTime: currentTime.format("HH:mm"),
        toTime: currentTime.add(15, "minute").format("HH:mm")
      });
    }
  };

  const handleDayChange = (day: string) => {
    const currentDays = selectedDays.length > 0 ? selectedDays : thresholdCrossingSettingScheduleData?.days || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d: string) => d !== day)
      : [...currentDays, day];
    setSelectedDays(newDays);
    setThresholdCrossingSettingScheduleData({ ...thresholdCrossingSettingScheduleData, days: sortedDays(newDays) });
    setErrMsg({ ...errMsg, days: "" });
  };

  const sortedDays = (days: string[]) => days.sort(
    (a, b) => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b)
  );

  const handleFromChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const newFromTime = newValue.format("HH:mm");
      const toTime = parseScheduleTime(thresholdCrossingSettingScheduleData.toTime);
      if (toTime.isValid() && newValue.isAfter(toTime)) {
        setThresholdCrossingSettingScheduleData({ ...thresholdCrossingSettingScheduleData, "fromTime": newFromTime, "toTime": newValue.add(15, "minute").format("HH:mm") });
      } else {
        setThresholdCrossingSettingScheduleData({ ...thresholdCrossingSettingScheduleData, "fromTime": newFromTime });
      }
      setErrMsg({ ...errMsg, "fromTime": "" });
    }
  };

  const handleToChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const fromTime = parseScheduleTime(thresholdCrossingSettingScheduleData.fromTime);
      setThresholdCrossingSettingScheduleData({ ...thresholdCrossingSettingScheduleData, "toTime": newValue.format("HH:mm") });
      if (!fromTime.isValid() || newValue.isAfter(fromTime)) {
        setErrMsg({ ...errMsg, "toTime": "" });
      } else {
        setErrMsg({ ...errMsg, "toTime": "End time must be greater than start time" });
      }
    }
  };

  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const activeDays = selectedDays.length > 0 ? selectedDays : thresholdCrossingSettingScheduleData?.days || [];

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button onClick={submitHandler} disabled={isDisabledSave || errorsPresent || !isDirty}>
        Save
      </Button>
    </>
  );

  const sheetTitle = `${modelOpenMode} schedule`;

  return (
    <Sheet open={true} onClose={handleClose} title={sheetTitle} footer={footer} width="520px">
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Description */}
          <p className="text-xs italic" style={{ color: "var(--warning)" }}>
            Set the days and time this profile will run.
          </p>

          {/* Days Selector */}
          <div className="space-y-2">
            <Label>Days</Label>
            <div className="flex gap-2 flex-wrap">
              {daysOfWeek.map((day) => (
                <button
                  key={day}
                  type="button"
                  name="days"
                  onClick={() => handleDayChange(day)}
                  className={`h-9 w-9 rounded-full text-sm font-medium border transition-colors ${activeDays.includes(day)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted text-muted-foreground border-border hover:opacity-80"
                    }`}
                
                >
                  {dayDisplayMap[day]}
                </button>
              ))}
            </div>
          </div>

          {/* All Day Switch */}
          <div className="flex items-center justify-between space-x-2">
            <Label htmlFor="all-day-switch" className="cursor-pointer">All Day</Label>
            <Switch
              id="all-day-switch"
              checked={isAllDay}
              onCheckedChange={handleAllDayChange}
            />
          </div>

          {/* Time Selector */}
          {!isAllDay && (
            <div className="space-y-2 mt-0">
              <div className="flex flex-row gap-4">
                <div className="flex flex-col gap-2 flex-1">
                  <Label>Start Time</Label>
                  
                  <Input type="time"
                    name={fromTimes}
                    value={parseScheduleTime(thresholdCrossingSettingScheduleData.fromTime).format("HH:mm")}
                    onChange={(e) => handleFromChange(parseScheduleTime(e.target.value))}
                    className="h-9" />
                  {errMsg?.fromTime && (
                    <p className="text-xs font-sans text-destructive">
                      {errMsg?.fromTime}
                    </p>
                  )}
                </div>
                <span className="text-muted-foreground mt-6">–</span>
                <div className="flex flex-col gap-2 flex-1">
                  <Label>End Time</Label>
                  <Input type="time"
                    name={toTimes}
                    value={parseScheduleTime(thresholdCrossingSettingScheduleData.toTime).format("HH:mm")}
                    onChange={(e) => handleToChange(parseScheduleTime(e.target.value))}
                    className="h-9" />
                  {errMsg?.toTime && (
                    <p className="text-xs font-sans text-destructive">
                      {errMsg?.toTime}
                    </p>
                  )}


                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </Sheet>
  );
};


export default AddEditProfileSchedule;
